import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  TemplateRef,
} from '@angular/core';
import { FileUploaderConfig } from './models/file-uploader-config.model';
import { FileUploaderFile, FileUploaderFileChunk } from './models/file-uploader-file.model';
import { FileUploaderStatus } from './models/file-uploader-status.enum';
import { Id } from '@ideom/http';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, concatMap, finalize, switchMap, take, tap } from 'rxjs/operators';
import { ComponentChanges } from '../../../models/component-changes.model';

@Component({
  selector: 'ideom-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploaderComponent implements OnDestroy, OnChanges {
  @Input() config: FileUploaderConfig;
  @Input() multiple: boolean;
  @Input() invalid = true;
  @Input() existing: FileUploaderFile[] = [];
  @Input() fileActionTemplate: TemplateRef<{ file: FileUploaderFile }>;

  @Output() filesChange = new EventEmitter<FileUploaderFile[]>();
  @Output() filesUpload = new EventEmitter<FileUploaderFile[]>();
  @Output() filesAdd = new EventEmitter<FileUploaderFile[]>();
  @Output() filesDelete = new EventEmitter<FileUploaderFile[]>();
  @Output() fileWasReplaced = new EventEmitter<FileUploaderFile>();

  files: FileUploaderFile[] = [];
  FileUploaderStatus = FileUploaderStatus;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: ComponentChanges<FileUploaderComponent>): void {
    if (changes.existing && this.existing) {
      this.files.push(...this.existing);
    }
  }

  onChange($event: Event): void {
    const files = (<HTMLInputElement>$event.target).files as FileList;
    this.handleFiles(files);
    this.startUpload();
  }

  onDrop($event: DragEvent): void {
    $event.preventDefault();
    const droppedFiles = $event.dataTransfer.files;
    if (this.multiple) {
      this.handleFiles(droppedFiles);
      this.startUpload();
    } else if (droppedFiles && droppedFiles.length > 0) {
      const fileForReplacement = droppedFiles[0];
      this.replaceFile(fileForReplacement);
      this.startUpload();
    }
  }

  onDragover($event: DragEvent): void {
    $event.preventDefault();
  }

  onDeleteFile(fileToDelete: FileUploaderFile): void {
    this.files = this.files.filter((file) => file !== fileToDelete);
    this.filesChange.emit(this.files);
    this.filesDelete.emit([fileToDelete]);
  }

  onCancelUploading(fileToCancel: FileUploaderFile): void {
    fileToCancel.status = FileUploaderStatus.Canceled;
    fileToCancel.subscription?.unsubscribe();
  }

  onResumeUploading(fileToResume: FileUploaderFile): void {
    this.scheduleUpload([fileToResume]);
  }

  ngOnDestroy(): void {
    this.files.forEach((file) => {
      file.subscription?.unsubscribe();
    });
  }

  private startUpload(): void {
    const filesToUpload = this.files.filter((file) => file.status === FileUploaderStatus.Pending);
    this.createChunks(filesToUpload);
    this.scheduleUpload(filesToUpload);
  }

  private scheduleUpload(files: FileUploaderFile[]): void {
    files.forEach((file) => {
      file.status = FileUploaderStatus.InProgress;
      file.errors = [];
      const chunks = file.chunks.filter((chunk) => [FileUploaderStatus.Canceled, FileUploaderStatus.Pending].includes(chunk.status));
      chunks.forEach((chunk, index) => {
        let observable$: Observable<Id | string[]> = of(null).pipe(
          tap(() => {
            chunk.status = FileUploaderStatus.InProgress;
            this.recalculateProgress(file);
          })
        );
        if (index === 0 && !file.fileId) {
          // For the first chunk we should emit fileId after uploading
          observable$ = observable$
            .pipe(switchMap(() => this.config.method(chunk.data, file.file.name, null, chunk.offset, file.file.size)))
            .pipe(
              tap((id: Id) => {
                file.fileId = id.id;
                file.fileIdSubject.next(id);
              })
            );
        } else {
          observable$ = observable$.pipe(switchMap(() => file.fileIdSubject)).pipe(
            take(1),
            switchMap((id) => this.config.method(chunk.data, file.file.name, id.id, chunk.offset, file.file.size))
          );
        }

        chunk.observable = observable$.pipe(
          tap(() => {
            chunk.status = FileUploaderStatus.Completed;
            this.recalculateProgress(file);
          }),
          catchError((errors: string[]) => {
            file.status = chunk.status = FileUploaderStatus.Error;
            file.subscription?.unsubscribe();
            file.errors = errors;
            this.recalculateProgress(file);
            return of(null);
          }),
          finalize(() => {
            if (chunk.status === FileUploaderStatus.InProgress) {
              chunk.status = FileUploaderStatus.Canceled;
            }
            this.recalculateProgress(file);
          })
        );
      });

      let observable$ = of(null);
      chunks.forEach((chunk) => {
        observable$ = observable$.pipe(concatMap(() => chunk.observable));
      });

      file.subscription = observable$.subscribe(
        () => {
          file.status = FileUploaderStatus.Completed;
          this.filesUpload.emit([file]);
        },
        () => {
          file.status = FileUploaderStatus.Error;
        }
      );
    });
  }

  private replaceFile(file: File): void {
    const fileModel = this.getFileModel(file);
    const previousFiles = this.files;
    this.files = [fileModel];
    this.filesChange.emit(this.files);
    this.filesAdd.emit(this.files);
    if (previousFiles.length > 0) {
      this.fileWasReplaced.emit(fileModel);
    }
  }

  private handleFiles(files: FileList): void {
    const filesToAdd = [];
    for (let index = 0; index < files.length; index++) {
      const file = files.item(index);
      const fileModel = this.getFileModel(file);
      filesToAdd.push(fileModel);
    }
    this.files.push(...filesToAdd);
    this.filesChange.emit(this.files);
    this.filesAdd.emit(filesToAdd);
  }

  private getFileModel(file: File): FileUploaderFile {
    return {
      file: file,
      fileSizeMb: file.size / (1024 * 1024),
      status: FileUploaderStatus.Pending,
      chunks: [],
      fileIdSubject: new ReplaySubject<Id>(1),
    };
  }

  private createChunks(files: FileUploaderFile[]): void {
    const chunkSize = this.config.chunkFileSize;
    files.forEach((file) => {
      let offset = 0;
      const end = file.file.size;
      do {
        const remaining = end - offset;
        const count = remaining < chunkSize ? remaining : chunkSize;
        const blob = file.file.slice(offset, offset + count);
        const chunk: FileUploaderFileChunk = {
          status: FileUploaderStatus.Pending,
          count,
          offset,
          data: blob,
        };
        file.chunks.push(chunk);
        offset += chunkSize;
      } while (offset < end);
      this.recalculateProgress(file);
    });
  }

  private recalculateProgress(file: FileUploaderFile): void {
    const chunksCompleted = file.chunks.filter((chunk) => chunk.status === FileUploaderStatus.Completed).length;
    const chunksProcessing = file.chunks.filter((chunk) => chunk.status === FileUploaderStatus.InProgress).length;
    file.progress = {
      chunksTotal: file.chunks.length,
      chunksCompleted,
      chunksProcessing,
      percentageCompleted: Math.round((chunksCompleted / file.chunks.length) * 100),
      percentageProcessing: Math.round(((chunksCompleted + chunksProcessing) / file.chunks.length) * 100),
    };
    this.cdr.markForCheck();
  }
}
